import { Button, ButtonVariant } from '@Core/Components'
import {
    ExternalReference,
    AuditEvent,
    Purchase,
    PurchaseStatus,
} from '@Purchasing/Domain/Model/PurchaseProcess'
import { roles } from '@Purchasing/roles'
import { formatBRL } from '@Purchasing/Utils/money'
import { format } from 'date-fns'
import { useState } from 'react'

// ---------------------------------------------------------------------------
// Tokens compartilhados do detalhe (Fase 3 polish)
// ---------------------------------------------------------------------------

export const detailCardClass =
    'rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800'

const labelClass =
    'text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500'

// ---------------------------------------------------------------------------

export const purchaseStatusLabel: Record<PurchaseStatus, string> = {
    DRAFT: 'Rascunho',
    ORDERED: 'Pedido realizado',
    PARTIALLY_RECEIVED: 'Recebido parcialmente',
    RECEIVED: 'Recebido',
    CHECKED: 'Conferido',
    CANCELED: 'Cancelada',
}

const purchaseStatusBadgeClass: Record<PurchaseStatus, string> = {
    DRAFT: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
    ORDERED: 'bg-primary text-primary-foreground',
    PARTIALLY_RECEIVED:
        'bg-warning-50 text-warning-600 dark:bg-warning-900/30 dark:text-warning-400',
    RECEIVED:
        'bg-od-primary-100 text-od-primary-800 dark:bg-od-primary-900/40 dark:text-od-primary-200',
    CHECKED:
        'bg-od-primary-100 text-od-primary-800 dark:bg-od-primary-900/40 dark:text-od-primary-200',
    CANCELED: 'bg-od-secondary-100 text-od-secondary-600 dark:bg-od-secondary-900/30 dark:text-od-secondary-200',
}

export const PurchaseStatusBadge = ({ status }: { status: PurchaseStatus }) => (
    <span
        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${purchaseStatusBadgeClass[status] ?? ''}`}
        data-testid="purchase-status-badge"
    >
        {purchaseStatusLabel[status] ?? status}
    </span>
)

/** Próximos status válidos por estado atual — espelha o backend (§14). */
const NEXT_STATUS: Record<PurchaseStatus, PurchaseStatus[]> = {
    DRAFT: ['ORDERED', 'CANCELED'],
    ORDERED: ['PARTIALLY_RECEIVED', 'RECEIVED', 'CANCELED'],
    PARTIALLY_RECEIVED: ['RECEIVED', 'CANCELED'],
    RECEIVED: ['CHECKED'],
    CHECKED: [],
    CANCELED: [],
}

const nextStatusLabel: Partial<Record<PurchaseStatus, string>> = {
    ORDERED: 'Marcar como pedido',
    PARTIALLY_RECEIVED: 'Recebimento parcial',
    RECEIVED: 'Marcar como recebido',
    CHECKED: 'Conferir recebimento',
    CANCELED: 'Cancelar compra',
}

// ---------------------------------------------------------------------------
// PurchaseSection (§28, §33–§35)
// ---------------------------------------------------------------------------

interface PurchaseSectionProps {
    processId: string
    purchase: Purchase | null
    loading?: boolean
    hasWinner: boolean
    onGenerate: () => void
    onChangeStatus: (status: PurchaseStatus) => void
    onSaveNotes: (notes: string) => void
    actionLoading?: boolean
    actionError?: string | null
}

export const PurchaseSection = ({
    processId,
    purchase,
    loading,
    hasWinner,
    onGenerate,
    onChangeStatus,
    onSaveNotes,
    actionLoading,
    actionError,
}: PurchaseSectionProps) => {
    const [editingNotes, setEditingNotes] = useState(false)
    const [notesDraft, setNotesDraft] = useState('')

    if (loading) {
        return (
            <section className={`${detailCardClass} mt-4`}>
                <p className="text-sm text-gray-400">Carregando compra…</p>
            </section>
        )
    }

    // Empty state sem compra (§28)
    if (!purchase) {
        return (
            <section className={`${detailCardClass} mt-4`} data-testid="purchase-section">
                <h2 className="mb-2 text-lg font-semibold">Compra</h2>
                <div className="rounded-md border border-dashed border-gray-300 p-6 text-center dark:border-gray-600">
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                        Nenhuma compra gerada.
                    </p>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        {hasWinner
                            ? 'Converta a cotação vencedora em uma compra real.'
                            : 'Selecione uma cotação vencedora para habilitar a geração da compra.'}
                    </p>
                    {hasWinner && (
                        <div className="mt-3">
                            <PermissionButton
                                permission={roles.purchase.permissions.create.key}
                                onClick={onGenerate}
                                disabled={actionLoading}
                                testId="generate-purchase"
                            >
                                {actionLoading ? 'Gerando…' : 'Gerar compra'}
                            </PermissionButton>
                        </div>
                    )}
                    {actionError && (
                        <p className="mt-2 text-sm text-od-secondary-600">{actionError}</p>
                    )}
                </div>
            </section>
        )
    }

    const quoteSnap = purchase.supplierQuoteSnapshot
    const nexts = NEXT_STATUS[purchase.status] ?? []

    return (
        <section className={`${detailCardClass} mt-4`} data-testid="purchase-section">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-lg font-semibold">Compra</h2>
                <PurchaseStatusBadge status={purchase.status} />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Field label="Fornecedor" value={
                    purchase.supplierSnapshot?.corporateName ??
                    purchase.supplierSnapshot?.tradeName ??
                    '-'
                } />
                <Field label="Cotação de origem" value={`#${(quoteSnap?.supplierQuoteId ?? '').slice(-8)}`} mono />
                <Field label="Total" value={formatBRL(purchase.totalCents)} strong />
                <Field label="Quantidade" value={`${purchase.quantity} un.`} />
                <Field label="Prazo" value={purchase.leadTimeDays ? `${purchase.leadTimeDays} dias` : '-'} />
                <Field label="Entrega esperada" value={fmtDate(purchase.expectedDeliveryDate)} />
                <Field label="Pagamento" value={purchase.paymentTerms ?? '-'} />
                <Field label="Emitida em" value={fmtDateTime(purchase.issuedAt)} />
            </div>

            {(purchase.orderedAt || purchase.receivedAt || purchase.checkedAt) && (
                <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <Field label="Pedida em" value={fmtDateTime(purchase.orderedAt)} />
                    <Field label="Recebida em" value={fmtDateTime(purchase.receivedAt)} />
                    <Field label="Conferida em" value={fmtDateTime(purchase.checkedAt)} />
                </div>
            )}

            {/* Notas operacionais — edição mínima (§34) */}
            <div className="mt-4">
                <span className={labelClass}>Observações</span>
                {editingNotes ? (
                    <div className="mt-1 flex flex-col gap-2 sm:flex-row">
                        <input
                            value={notesDraft}
                            onChange={(e) => setNotesDraft(e.target.value)}
                            placeholder="Observações da compra"
                            className="w-full rounded-md border border-gray-300 bg-white px-2 py-1 text-sm focus:border-od-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                            data-testid="purchase-notes-input"
                        />
                        <div className="flex gap-2">
                            <Button
                                variant={ButtonVariant.primary}
                                onClick={() => {
                                    onSaveNotes(notesDraft)
                                    setEditingNotes(false)
                                }}
                                disabled={actionLoading}
                            >
                                Salvar
                            </Button>
                            <Button
                                variant={ButtonVariant.secondary}
                                onClick={() => setEditingNotes(false)}
                            >
                                Cancelar
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center gap-2">
                        <p className="text-sm text-gray-700 dark:text-gray-200">
                            {purchase.notes || '—'}
                        </p>
                        <button
                            type="button"
                            className="text-xs text-gray-500 underline hover:text-gray-800 dark:hover:text-gray-200"
                            onClick={() => {
                                setNotesDraft(purchase.notes ?? '')
                                setEditingNotes(true)
                            }}
                            data-testid="edit-purchase-notes"
                        >
                            Editar
                        </button>
                    </div>
                )}
            </div>

            {/* Transições de status (§35) */}
            {nexts.length > 0 && (
                <div className="mt-4 flex flex-wrap items-center gap-2">
                    <span className={labelClass}>Ações</span>
                    {nexts.map((s) => (
                        <Button
                            key={s}
                            variant={
                                s === 'CANCELED'
                                    ? ButtonVariant.secondary
                                    : ButtonVariant.primary
                            }
                            disabled={actionLoading}
                            onClick={() => onChangeStatus(s)}
                            data-testid={`purchase-action-${s.toLowerCase()}`}
                        >
                            {nextStatusLabel[s] ?? s}
                        </Button>
                    ))}
                </div>
            )}
            {nexts.length === 0 && (
                <p className="mt-3 text-xs italic text-gray-400">
                    {purchase.status === 'CHECKED'
                        ? 'Compra conferida — fluxo operacional concluído.'
                        : 'Compra cancelada.'}
                </p>
            )}
            {actionError && (
                <p className="mt-2 text-sm text-od-secondary-600">{actionError}</p>
            )}
        </section>
    )
}

// ---------------------------------------------------------------------------
// AuditTimeline (§30)
// ---------------------------------------------------------------------------

export const AuditTimeline = ({
    events,
    loading,
    actionLabels,
    hiddenActions,
}: {
    events: AuditEvent[]
    loading?: boolean
    actionLabels: Record<string, string>
    /** Eventos técnicos que não geram ruído visual (§27/§30). */
    hiddenActions?: string[]
}) => {
    if (loading) return <p className="text-sm text-gray-400">Carregando…</p>
    if (!events.length)
        return (
            <p className="text-sm text-gray-500 dark:text-gray-400">
                Nenhuma atividade registrada.
            </p>
        )

    const visible = events.filter(
        (e) => !(hiddenActions ?? []).includes(e.action)
    )
    if (!visible.length)
        return (
            <p className="text-sm text-gray-500 dark:text-gray-400">
                Nenhuma atividade registrada.
            </p>
        )

    return (
        <ol className="relative space-y-4 border-l border-gray-200 pl-5 dark:border-gray-700">
            {visible.map((event) => (
                <li key={event._id} className="relative">
                    <span
                        className="absolute -left-[26px] top-1 h-2.5 w-2.5 rounded-full border-2 border-white bg-od-primary-500 dark:border-gray-800"
                        aria-hidden
                    />
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                        {format(new Date(event.createdAt), 'dd/MM/yyyy HH:mm')}
                    </p>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {actionLabels[event.action] ?? 'Evento atualizado'}
                    </p>
                    {event.actor?.name && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            por {event.actor.name}
                        </p>
                    )}
                </li>
            ))}
        </ol>
    )
}

// ---------------------------------------------------------------------------
// ExternalReferencesCard compacto (§29)
// ---------------------------------------------------------------------------

export const ExternalReferencesList = ({
    references,
}: {
    references: ExternalReference[]
}) => {
    if (!references.length)
        return (
            <p className="text-sm text-gray-500 dark:text-gray-400">
                Nenhuma referência externa registrada.
            </p>
        )

    return (
        <ul className="flex flex-wrap gap-2">
            {references.map((ref, i) => (
                <li
                    key={`${ref.system}-${ref.entityType}-${i}`}
                    className={chipClassPurchasing}
                    title={`${ref.system} / ${ref.entityType} / ${ref.externalId}`}
                >
                    {ref.system} • {ref.entityType} •{' '}
                    <span className="font-mono">
                        {String(ref.externalId).slice(0, 8)}…
                    </span>
                </li>
            ))}
        </ul>
    )
}

const chipClassPurchasing =
    'inline-flex max-w-full items-center rounded-full border border-gray-200 px-2.5 py-0.5 text-xs text-gray-600 dark:border-gray-600 dark:text-gray-300 overflow-hidden whitespace-nowrap'

// ---------------------------------------------------------------------------
// Helpers internos
// ---------------------------------------------------------------------------

const Field = ({
    label,
    value,
    strong,
    mono,
}: {
    label: string
    value: React.ReactNode
    strong?: boolean
    mono?: boolean
}) => (
    <div>
        <span className={labelClass}>{label}</span>
        <p
            className={`mt-0.5 truncate text-sm ${
                strong
                    ? 'font-semibold text-gray-900 dark:text-gray-100'
                    : 'text-gray-700 dark:text-gray-200'
            } ${mono ? 'font-mono' : ''}`}
            title={typeof value === 'string' ? value : undefined}
        >
            {value ?? '-'}
        </p>
    </div>
)

const fmtDate = (d: any) =>
    d ? format(new Date(d), 'dd/MM/yyyy') : '-'
const fmtDateTime = (d: any) =>
    d ? format(new Date(d), 'dd/MM/yyyy HH:mm') : '-'

// Import local para não circular no topo
import { PermissionWrapper } from '@Core/Components/PermissionWrapper/PermissionWrapper'
function PermissionButton({
    permission,
    onClick,
    disabled,
    children,
    testId,
}: {
    permission: string
    onClick: () => void
    disabled?: boolean
    children: React.ReactNode
    testId?: string
}) {
    return (
        <PermissionWrapper permission={permission}>
            <Button
                variant={ButtonVariant.primary}
                onClick={onClick}
                disabled={disabled}
                data-testid={testId}
            >
                {children}
            </Button>
        </PermissionWrapper>
    )
}
